/**
 * errorText, one readable sentence out of whatever a failure produced.
 *
 * This rule already existed, privately, inside `utils/toast.js`, and it existed
 * because of two real failures seen in this app:
 *
 * 1. An ERROR OBJECT rendered straight into the UI, which printed
 *    "[object Object]".
 * 2. A WHOLE HTML DOCUMENT. When the API is unreachable the dev proxy answers
 *    with its own HTML error page and axios carries that markup through as the
 *    error string, so the screen showed a stack of angle brackets.
 *
 * Empty states had the same two problems and no fallback at all: several of
 * them passed the raw `error` value as their body text. So the rule moves here,
 * `toast.js` now calls it, and every load failure in the app fails to the SAME
 * sentence instead of to nine different ones or to nothing.
 *
 * Nothing here touches a request, a payload or a query key. It only decides
 * what a person reads when one of them fails.
 */

/** The fallback for a toast, where the action that failed is already implied. */
export const GENERIC_FAILURE = 'Something went wrong. Please try again.';

/**
 * The fallback for a screen or a table that could not READ. One sentence, used
 * by `ConsoleError` and by every hand-written load-failure empty state, so the
 * app has a single voice when a fetch breaks.
 */
export const LOAD_FAILURE = 'Something went wrong while loading this. Try again in a moment.';

export function errorText(value, fallback = GENERIC_FAILURE) {
  const raw =
    typeof value === 'string'
      ? value
      : (value && (value.message || value.detail || value.error)) || '';

  const text = String(raw).trim();
  if (!text) return fallback;
  // Anything markup shaped is refused: it tells the user nothing and looks
  // broken, which is worse than a plain sentence.
  if (/^\s*<|<!doctype|<html|<\/?[a-z][\s\S]*>/i.test(text)) return fallback;
  // A server stack trace or a wall of prose is not a message either.
  if (text.length > 200) return fallback;
  return text;
}

export default errorText;
